/**
 * 投放词库表格组件
 * 已重构: 列配置和添加弹窗已抽取为独立模块
 * 
 * 原文件: 475行 -> 重构后: 3个文件，每个<200行
 */
import React, { useState } from 'react';
import { Table, Button, Space, Input, Select, Popconfirm, Form } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { TargetingKeyword, KEYWORD_TYPE_OPTIONS } from './types';
import { getTargetingKeywordColumns } from './TargetingKeywordColumns';
import { AddTargetingKeywordModal } from './AddTargetingKeywordModal';


interface TargetingKeywordTableProps {
    keywords: TargetingKeyword[];
    loading: boolean;
    onAdd: (data: Partial<TargetingKeyword>) => Promise<any>;
    onBulkImport?: (keywords: Array<{
        keyword: string;
        keyword_type?: string;
        match_type?: string;
        conversion_rate?: number;
        priority?: number;
    }>) => Promise<any>;
    onUpdate: (id: number, data: Partial<TargetingKeyword>) => Promise<any>;
    onDelete: (id: number) => Promise<any>;
    onBatchDelete: (ids: number[]) => Promise<any>;
    onBatchUpdateStatus: (ids: number[], isActive: boolean) => Promise<any>;
}

export const TargetingKeywordTable: React.FC<TargetingKeywordTableProps> = ({
    keywords,
    loading,
    onAdd,
    onBulkImport,
    onUpdate,
    onDelete,
    onBatchDelete,
    onBatchUpdateStatus,
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState<string | undefined>();
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm] = Form.useForm();
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [addForm] = Form.useForm();

    // 过滤数据
    const safeKeywords = Array.isArray(keywords) ? keywords : [];
    const filteredData = safeKeywords.filter(k => {
        const matchSearch = !searchText || k.keyword.toLowerCase().includes(searchText.toLowerCase());
        const matchType = !filterType || k.keyword_type === filterType;
        return matchSearch && matchType;
    });

    // 获取列配置
    const columns = getTargetingKeywordColumns({
        editingId,
        editForm,
        onUpdate,
        onDelete,
        onEdit: (record) => {
            setEditingId(record.id);
            editForm.setFieldsValue(record);
        },
    });

    const handleAdd = async () => {
        try {
            const values = await addForm.validateFields();
            const keywordText = values.keyword?.trim() || '';
            const keywordList = keywordText.split(/[\n\r]+/).map((k: string) => k.trim()).filter((k: string) => k.length > 0);

            if (keywordList.length === 0) return;

            if (keywordList.length === 1) {
                await onAdd({
                    keyword: keywordList[0],
                    keyword_type: values.keyword_type,
                    match_type: values.match_type,
                    conversion_rate: values.conversion_rate,
                    search_volume: values.search_volume,
                    priority: values.priority,
                    estimated_cpc: values.estimated_cpc,
                    notes: values.notes,
                });
            } else if (onBulkImport) {
                await onBulkImport(keywordList.map((kw: string) => ({
                    keyword: kw,
                    keyword_type: values.keyword_type,
                    match_type: values.match_type,
                    conversion_rate: values.conversion_rate,
                    priority: values.priority,
                    estimated_cpc: values.estimated_cpc,
                })));
            } else {
                for (const kw of keywordList) {
                    await onAdd({ keyword: kw, keyword_type: values.keyword_type, match_type: values.match_type, priority: values.priority });
                }
            }

            setAddModalVisible(false);
            addForm.resetFields();
        } catch (error) {
            console.error('添加失败:', error);
        }
    };

    const handleBatchDelete = async () => {
        if (selectedRowKeys.length === 0) return;
        await onBatchDelete(selectedRowKeys as number[]);
        setSelectedRowKeys([]);
    };

    const handleBatchEnable = async () => {
        if (selectedRowKeys.length === 0) return;
        await onBatchUpdateStatus(selectedRowKeys as number[], true);
        setSelectedRowKeys([]);
    };

    const handleBatchDisable = async () => {
        if (selectedRowKeys.length === 0) return;
        await onBatchUpdateStatus(selectedRowKeys as number[], false);
        setSelectedRowKeys([]);
    };

    return (
        <div className="targeting-keyword-table">
            {/* 工具栏 */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <Space wrap>
                    <Input
                        placeholder="搜索关键词"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Select
                        placeholder="词性筛选"
                        value={filterType}
                        onChange={setFilterType}
                        style={{ width: 140 }}
                        allowClear
                        options={Object.entries(KEYWORD_TYPE_OPTIONS).map(([value, label]) => ({ value, label }))}
                    />
                </Space>

                <Space wrap>
                    {selectedRowKeys.length > 0 && (
                        <>
                            <Button onClick={handleBatchEnable}>批量启用</Button>
                            <Button onClick={handleBatchDisable}>批量停用</Button>
                            <Popconfirm title={`确定删除 ${selectedRowKeys.length} 个关键词?`} onConfirm={handleBatchDelete}>
                                <Button danger icon={<DeleteOutlined />}>批量删除</Button>
                            </Popconfirm>
                        </>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>
                        添加关键词
                    </Button>
                </Space>
            </div>

            {/* 表格 */}
            <Table
                rowKey="id"
                columns={columns}
                dataSource={filteredData || []}
                loading={loading}
                size="small"
                scroll={{ x: 1000 }}
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条`,
                    defaultPageSize: 20,
                }}
            />

            {/* 添加弹窗 */}
            <AddTargetingKeywordModal
                visible={addModalVisible}
                form={addForm}
                onOk={handleAdd}
                onCancel={() => {
                    setAddModalVisible(false);
                    addForm.resetFields();
                }}
            />
        </div>
    );
};

export default TargetingKeywordTable;
